import { identifyEnvironment } from "@jsq/utils";

export type PublicConfig = {
  dataGrailCustomerId: string;
  dataGrailContainerId: string;
  heapId: string;
  heapDataConsentId: string;
  googleMapsBrowserApiKey: string;
};

// None of these are secrets: DataGrail/Heap ids and Google's *browser* Maps key
// (referrer-restricted) are all readable in page source by design. Same treatment
// as our other browser-exposed keys — Datadog RUM, Unleash — none of which go
// through Secrets Manager.
const DATA_GRAIL_CUSTOMER_ID = "5d7f5fe3-3e89-4c47-8d8b-1b2841d5112e";
const DATA_GRAIL_CONTAINER_PROD = "cc0725be-8d79-41e0-a1e7-5157051e0a4b";
const DATA_GRAIL_CONTAINER_TEST = "edba4c39-2250-4cb9-8f39-286c9662206d";

const HEAP_ID_PROD = "2727357492";
const HEAP_CONSENT_PROD = "3Gvrbpmm2mVyuSWSiLV8pD";
const HEAP_ID_TEST = "2234022012";
const HEAP_CONSENT_TEST = "MSRWnAHBc9dnmcKOV66ai";

const MAPS_KEY_PROD = "AIzaSyCGlQQqt528NPtRcCPvI3RVp0wOfq8WQrg";
const MAPS_KEY_TEST = "AIzaSyB1WBOwGAkED_8cuYWsqD5BzE_xALtTTr4";

const EMPTY: PublicConfig = {
  dataGrailCustomerId: "",
  dataGrailContainerId: "",
  heapId: "",
  heapDataConsentId: "",
  googleMapsBrowserApiKey: "",
};

const PUBLIC_CONFIG_BY_ENV: Partial<Record<string, PublicConfig>> = {
  production: {
    dataGrailCustomerId: DATA_GRAIL_CUSTOMER_ID,
    dataGrailContainerId: DATA_GRAIL_CONTAINER_PROD,
    heapId: HEAP_ID_PROD,
    heapDataConsentId: HEAP_CONSENT_PROD,
    googleMapsBrowserApiKey: MAPS_KEY_PROD,
  },
  cit: {
    dataGrailCustomerId: DATA_GRAIL_CUSTOMER_ID,
    dataGrailContainerId: DATA_GRAIL_CONTAINER_PROD,
    heapId: "",
    heapDataConsentId: "",
    googleMapsBrowserApiKey: MAPS_KEY_PROD,
  },
  // QA intentionally runs without DataGrail: no consent banner for internal QA
  // traffic. Staging keeps the test container so the banner stays exercised
  // pre-prod.
  qa: {
    dataGrailCustomerId: "",
    dataGrailContainerId: "",
    heapId: HEAP_ID_TEST,
    heapDataConsentId: HEAP_CONSENT_TEST,
    googleMapsBrowserApiKey: MAPS_KEY_TEST,
  },
  staging: {
    dataGrailCustomerId: DATA_GRAIL_CUSTOMER_ID,
    dataGrailContainerId: DATA_GRAIL_CONTAINER_TEST,
    heapId: HEAP_ID_TEST,
    heapDataConsentId: HEAP_CONSENT_TEST,
    googleMapsBrowserApiKey: MAPS_KEY_TEST,
  },
  perf: {
    dataGrailCustomerId: DATA_GRAIL_CUSTOMER_ID,
    dataGrailContainerId: DATA_GRAIL_CONTAINER_TEST,
    heapId: HEAP_ID_TEST,
    heapDataConsentId: HEAP_CONSENT_TEST,
    googleMapsBrowserApiKey: MAPS_KEY_TEST,
  },
  prodops: { ...EMPTY, googleMapsBrowserApiKey: MAPS_KEY_PROD },
  evals: { ...EMPTY, googleMapsBrowserApiKey: MAPS_KEY_PROD },
  test: { ...EMPTY, googleMapsBrowserApiKey: MAPS_KEY_TEST },
  development: { ...EMPTY, googleMapsBrowserApiKey: MAPS_KEY_TEST },
  preview: { ...EMPTY, googleMapsBrowserApiKey: MAPS_KEY_TEST },
  communal: { ...EMPTY, googleMapsBrowserApiKey: MAPS_KEY_TEST },
};

export function getPublicConfig(hostname?: string): PublicConfig {
  const { environmentType } = identifyEnvironment(hostname);
  return PUBLIC_CONFIG_BY_ENV[environmentType] ?? EMPTY;
}
